/**
 * `v3.runs.*` — starting, reading, watching, stopping, resuming, renaming and prioritizing
 * runs, for members, scoped to the caller's customer. A `runId` takes a run id or a run key (`SC-52`).
 *
 * Handlers: `v3-runs.router.ts` (A-RUNS), except the body of `v3.runs.watch`, which is
 * `RunEventHub.watch` (A-STREAM).
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import {
  RunDetail,
  RunEvent,
  RunListInput,
  RunPage,
  RunPrioritizeInput,
  RunRef,
  RunRenameInput,
  RunStartInput,
  RunStartResult,
  RunSummary,
  RunWatchInput,
} from "@fm/v3";
import { Schema } from "effect";
import {
  DatabaseError,
  Forbidden,
  InvalidStateTransition,
  RecordNotFoundError,
  Unauthorized,
  ValidationError,
} from "../errors.ts";
import { LoopActorMiddleware } from "../loop-actor.tags.ts";

const errors = Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError);
const transitionErrors = Schema.Union(
  Unauthorized,
  Forbidden,
  RecordNotFoundError,
  ValidationError,
  DatabaseError,
  InvalidStateTransition,
);

/**
 * @since 1.0.0
 */
export class V3RunRpcs extends RpcGroup.make(
  /** Validates inputs against the loop's current definition, writes the run, dispatches its host. */
  Rpc.make("v3.runs.start", { payload: RunStartInput, success: RunStartResult, error: transitionErrors })
    .middleware(LoopActorMiddleware),
  Rpc.make("v3.runs.list", { payload: RunListInput, success: RunPage, error: errors }).middleware(LoopActorMiddleware),
  Rpc.make("v3.runs.get", { payload: RunRef, success: RunDetail, error: errors }).middleware(LoopActorMiddleware),
  /**
   * Every event after `afterSeq`: stored ones first, then live, pushed as the host appends
   * them. Ends once `run.finished` and `run.reviewed` have both been sent, or 15 minutes
   * after `run.finished` when no review follows.
   */
  Rpc.make("v3.runs.watch", { payload: RunWatchInput, success: RunEvent, error: errors, stream: true })
    .middleware(LoopActorMiddleware),
  /** Stops the host (its work is kept); refused on a finished run. */
  Rpc.make("v3.runs.stop", { payload: RunRef, success: RunSummary, error: transitionErrors })
    .middleware(LoopActorMiddleware),
  /** Starts a new run of the same loop version from the stopped run's kept work. */
  Rpc.make("v3.runs.resume", { payload: RunRef, success: RunStartResult, error: transitionErrors })
    .middleware(LoopActorMiddleware),
  /** Gives the run a new name; any member of the run's customer may. */
  Rpc.make("v3.runs.rename", { payload: RunRenameInput, success: RunSummary, error: errors })
    .middleware(LoopActorMiddleware),
  /** Sets an unfinished run's priority, or moves a queued run just before or after another queued run. */
  Rpc.make("v3.runs.prioritize", { payload: RunPrioritizeInput, success: RunSummary, error: transitionErrors })
    .middleware(LoopActorMiddleware),
) {}
